import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "tl_theme_preference";

export function ThemeProvider({ children }) {
  // Saved preference: 'light' | 'dark' | 'system'
  const [themePreference, setThemePreferenceState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "light" || saved === "dark" || saved === "system") {
        return saved;
      }
      return "system";
    } catch {
      return "system";
    }
  });

  // Resolved active theme: 'light' | 'dark'
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "light" || saved === "dark") {
        return saved;
      }
      if (typeof window !== "undefined" && window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      }
    } catch {}
    return "light";
  });

  // Applies the theme to DOM (attributes, classes, colorScheme, meta tags)
  const applyTheme = useCallback((theme) => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;

    root.setAttribute("data-theme", theme);
    if (theme === "dark") {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
    }

    try {
      root.style.colorScheme = theme;
    } catch {}

    // Update browser navigation bar color (mobile Chrome/Safari)
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", theme === "dark" ? "#14110f" : "#faf8f4");
    }
  }, []);

  // Synchronize on mount and whenever themePreference or system settings change
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const syncTheme = () => {
      let active;
      if (themePreference === "system") {
        active = mediaQuery.matches ? "dark" : "light";
      } else {
        active = themePreference;
      }
      setResolvedTheme(active);
      applyTheme(active);
    };

    syncTheme();

    const handleSystemChange = (e) => {
      if (themePreference === "system") {
        const sysTheme = e.matches ? "dark" : "light";
        setResolvedTheme(sysTheme);
        applyTheme(sysTheme);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleSystemChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleSystemChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleSystemChange);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleSystemChange);
      }
    };
  }, [themePreference, applyTheme]);

  // Explicitly set theme preference ('light' | 'dark' | 'system')
  const setThemePreference = useCallback((pref) => {
    const valid = pref === "dark" || pref === "light" ? pref : "system";
    setThemePreferenceState(valid);

    try {
      localStorage.setItem(STORAGE_KEY, valid);
    } catch {}

    let newResolved;
    if (valid === "system") {
      newResolved = (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches)
        ? "dark"
        : "light";
    } else {
      newResolved = valid;
    }

    setResolvedTheme(newResolved);
    applyTheme(newResolved);
  }, [applyTheme]);

  // Quick toggle between 'light' and 'dark'
  const toggleTheme = useCallback(() => {
    const nextTheme = resolvedTheme === "dark" ? "light" : "dark";
    setThemePreference(nextTheme);
  }, [resolvedTheme, setThemePreference]);

  return (
    <ThemeContext.Provider
      value={{
        theme: resolvedTheme,
        isDark: resolvedTheme === "dark",
        themePreference,
        setThemePreference,
        toggleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
